import { useEffect } from 'react';
import { motion } from 'motion/react';
import { styled } from '@mui/material/styles';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import useLocales from '../hooks/useLocales';
import useIntercomMessenger from '../hooks/useIntercomMessenger';
import { MotionContainer, varBounce } from './animate';
import { SeverErrorIllustration } from '../assets';
import Iconify from './Iconify';
import { INTERCOM_ENABLED } from '../config';

// ----------------------------------------------------------------------

const ContentStyle = styled('div')(({ theme }) => ({
    maxWidth: 480,
    margin: 'auto',
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    padding: theme.spacing(12, 0),
}));

const InlineContentStyle = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    textAlign: 'center',
    padding: theme.spacing(3),
}));

// ----------------------------------------------------------------------

type Props = {
    error?: Error | { message: string };
    resetErrorBoundary?: () => void;
    fullScreen?: boolean;
};

export default function ErrorScreen({ error, resetErrorBoundary, fullScreen = false }: Props) {
    const { translate } = useLocales();
    const { show: showIntercom, isReady: isIntercomReady } = useIntercomMessenger();

    useEffect(() => {
        if (error) {
            Sentry.captureException(error);
        }
    }, [error]);

    const handleRefresh = () => {
        window.location.reload();
    };

    const handleGoHome = () => {
        window.location.href = '/';
    };

    const handleGetSupport = () => {
        if (isIntercomReady) {
            showIntercom();
        }
    };

    if (!fullScreen) {
        return (
            <InlineContentStyle>
                <Iconify
                    icon="eva:alert-circle-outline"
                    sx={{ width: 48, height: 48, color: 'error.main', mb: 2 }}
                />
                <Typography variant="body1" sx={{ color: 'error.main', mb: 1 }}>
                    {translate('components.errorScreen.somethingWrong')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                    {translate('components.errorScreen.errorReported')}
                </Typography>
                {error?.message && (
                    <Typography
                        variant="caption"
                        sx={{ color: 'text.disabled', mb: 2, wordBreak: 'break-word' }}
                    >
                        {error.message}
                    </Typography>
                )}
                <Stack direction="row" spacing={1}>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleRefresh}
                        startIcon={<Iconify icon="eva:refresh-outline" />}
                    >
                        {translate('components.errorScreen.refresh')}
                    </Button>
                    {resetErrorBoundary && (
                        <Button variant="text" size="small" onClick={resetErrorBoundary}>
                            {translate('common.retry')}
                        </Button>
                    )}
                </Stack>
            </InlineContentStyle>
        );
    }

    return (
        <Container component={MotionContainer}>
            <ContentStyle sx={{ textAlign: 'center', alignItems: 'center' }}>
                <motion.div variants={varBounce().in}>
                    <Typography variant="h3" paragraph>
                        {translate('components.errorScreen.somethingWrong')}
                    </Typography>
                </motion.div>

                <motion.div variants={varBounce().in}>
                    <Typography sx={{ color: 'text.secondary' }}>
                        {translate('components.errorScreen.description')}
                    </Typography>
                </motion.div>

                <motion.div variants={varBounce().in}>
                    <SeverErrorIllustration
                        aria-hidden
                        sx={{ height: 200, my: { xs: 4, sm: 6 } }}
                    />
                </motion.div>

                <motion.div variants={varBounce().in}>
                    <Stack spacing={2} sx={{ mb: 3 }} alignItems="center">
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <Button
                                size="large"
                                variant="contained"
                                onClick={handleRefresh}
                                startIcon={<Iconify icon="eva:refresh-outline" />}
                            >
                                {translate('components.errorScreen.refresh')}
                            </Button>
                            <Button
                                size="large"
                                variant="outlined"
                                onClick={handleGoHome}
                                startIcon={<Iconify icon="eva:home-outline" />}
                            >
                                {translate('components.errorScreen.goHome')}
                            </Button>
                        </Stack>
                        {INTERCOM_ENABLED && (
                            <Button
                                size="large"
                                variant="text"
                                onClick={handleGetSupport}
                                startIcon={<Iconify icon="eva:message-circle-outline" />}
                            >
                                {translate('components.errorScreen.getSupport')}
                            </Button>
                        )}
                    </Stack>
                </motion.div>

                {error?.message && (
                    <motion.div variants={varBounce().in}>
                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 1,
                                bgcolor: 'error.lighter',
                                maxWidth: '100%',
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{ color: 'error.darker', fontWeight: 600 }}
                            >
                                {translate('components.errorScreen.errorDetails')}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'error.dark',
                                    mt: 0.5,
                                    wordBreak: 'break-word',
                                }}
                            >
                                {error.message}
                            </Typography>
                        </Box>
                    </motion.div>
                )}
            </ContentStyle>
        </Container>
    );
}
